import {createSlice, PayloadAction} from '@reduxjs/toolkit';

/**
 * Dock state for the Adaptive Intelligence assistant.
 *
 * This lives in redux rather than in the dock component because <Layout> is mounted twice in
 * Router.tsx (once for the "/" exact route, once for everything else). Crossing that boundary
 * remounts the whole subtree, so component state would drop the open panel and the selected
 * session, which is exactly the bug the older AIPanel has.
 */

export type AssistantDockMode = 'docked' | 'overlay' | 'fullscreen' | 'collapsed';

export interface IAiAssistantState {
    /** Whether the dock is showing at all. Transient: never persisted. */
    isOpen: boolean;
    /** The mode the user asked for. The rendered mode may degrade on a narrow viewport. */
    mode: AssistantDockMode;
    /** Docked width in pixels, owned by the splitter. */
    widthPx: number;
    /** The session the dock is showing. Transient: a reload lands back on the session list. */
    activeSessionId?: string;
}

/** Narrower than this and a docked panel leaves the app unusable, so it degrades to an overlay. */
export const DOCK_MIN_WIDTH = 320;
export const DOCK_MAX_WIDTH = 720;
export const DOCK_DEFAULT_WIDTH = 420;

export const persistKey = 'aiAssistant';

const defaultState: IAiAssistantState = {
    isOpen: false,
    mode: 'docked',
    widthPx: DOCK_DEFAULT_WIDTH,
    activeSessionId: undefined,
};

const clampWidth = (value: number): number =>
    Math.min(DOCK_MAX_WIDTH, Math.max(DOCK_MIN_WIDTH, Math.round(value)));

const isMode = (value: unknown): value is AssistantDockMode =>
    value === 'docked' || value === 'overlay' || value === 'fullscreen' || value === 'collapsed';

/**
 * Read the persisted mode and width straight out of localStorage for the very first render.
 *
 * PersistGate is not mounted in index.tsx, so redux-persist rehydrates a tick or two after first
 * paint. Without this the dock would paint at the default width and then visibly jump to the user's
 * width. redux-persist stores one JSON object whose values are themselves JSON, hence the double
 * parse. Any failure here is not worth a crash: fall back to the defaults.
 */
const readPersistedState = (): IAiAssistantState => {
    try {
        const raw = window.localStorage?.getItem(`persist:${persistKey}`);
        if (!raw) return defaultState;
        const outer = JSON.parse(raw) as { [key: string]: string };
        const mode = outer.mode != null ? JSON.parse(outer.mode) : undefined;
        const widthPx = outer.widthPx != null ? JSON.parse(outer.widthPx) : undefined;
        return {
            ...defaultState,
            mode: isMode(mode) ? mode : defaultState.mode,
            widthPx: typeof widthPx === 'number' && isFinite(widthPx) ? clampWidth(widthPx) : defaultState.widthPx,
        };
    } catch {
        return defaultState;
    }
};

const initialState: IAiAssistantState = readPersistedState();

const aiAssistantSlice = createSlice({
    name: 'aiAssistant',
    initialState,
    reducers: {
        openAssistant(state) {
            state.isOpen = true;
            // Reopening from the collapsed rail should show something, not the rail again.
            if (state.mode === 'collapsed') state.mode = 'docked';
        },
        closeAssistant(state) {
            state.isOpen = false;
        },
        toggleAssistant(state) {
            state.isOpen = !state.isOpen;
            if (state.isOpen && state.mode === 'collapsed') state.mode = 'docked';
        },
        setAssistantMode(state, action: PayloadAction<AssistantDockMode>) {
            state.mode = action.payload;
            if (action.payload !== 'collapsed') state.isOpen = true;
        },
        setAssistantWidth(state, action: PayloadAction<number>) {
            state.widthPx = clampWidth(action.payload);
        },
        setActiveAssistantSession(state, action: PayloadAction<string | undefined>) {
            state.activeSessionId = action.payload;
        },
    },
});

export const {
    openAssistant,
    closeAssistant,
    toggleAssistant,
    setAssistantMode,
    setAssistantWidth,
    setActiveAssistantSession,
} = aiAssistantSlice.actions;

export default aiAssistantSlice.reducer;
